import {
  useApiInfiniteQuery,
  useRetryApiInfiniteQuery,
  type Api,
} from "../../../services/api";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import type { PracticeRailCorpus } from "./usePracticeRailTerms";

/**
 * One article of the practice feed. It is a plain wire article — the practice
 * endpoints return the corpus a Boolean matched, not a ranked personal feed, so
 * there is no `recommendation` to carry and nothing here pretends otherwise.
 */
export type PracticeFeedArticle =
  Api["get"]["/webapp/private/practice-profiles/:uid/publications"]["output"]["body"]["docs"][number];

/**
 * A DISCRIMINATED UNION, because the three answers are not the same answer.
 *
 * The flat shape this replaces carried `articles` and `isLoading` side by side,
 * so a failed request read back as `{ articles: [], isLoading: false }` — which
 * the page renders as "we hold no publication for this practice yet". That is
 * the one dishonest empty state on this surface: the endpoint returns a
 * DELIBERATE 501 for a practice whose Boolean it cannot evaluate, and flattening
 * it into an empty corpus destroys the distinction it exists to draw.
 */
export type PracticeFeedResult =
  | { state: "pending" }
  | {
      state: "error";
      message: string;
      status: number | null;
      /** Asks the feed again. */
      retry: () => void;
      /** A retry is in flight. */
      isRetrying: boolean;
    }
  | {
      state: "ready";
      articles: PracticeFeedArticle[];
      /**
       * Each article's MAJOR MeSH descriptors, keyed by article id — the card
       * chip's raw material, and the same predicate (`e ? 'm'`) the rail counts
       * on. That equality is what keeps a card chip selectable in the rail
       * instead of being a dead end.
       *
       * A join and not a widened article, because the endpoint serves it as a
       * SIDECAR beside `docs`: `populateArticles` is the shared serializer of
       * every content endpoint in the app, and folding MeSH into it would put a
       * descriptor array on every article the API has ever returned for one
       * consumer's benefit. This hook is that consumer, so it does the join.
       *
       * An article ALWAYS has an entry (the endpoint guarantees one per doc, empty
       * when the paper carries no major descriptor), so a missing key is a bug and
       * not "not loaded".
       */
      majorDescriptorUisByArticleId: ReadonlyMap<string, readonly string[]>;
      // Unlike `canFetchNextPage`, stays true while a page loads: the
      // end-of-feed card reads this so a background refetch does not blink it.
      hasNextPage: boolean;
      canFetchNextPage: boolean;
      // The page's foot draws its loading rows on this, not on `isFetching`,
      // which a background refetch of the loaded pages also raises.
      isFetchingNextPage: boolean;
      fetchNextPage: () => void;
    };

const PAGE_SIZE = 20;

/**
 * What the feed is reading: one practice, optionally narrowed to one rail chip.
 *
 * `descriptorUi` is a rail term's id and NOT a Boolean or a `leafKey` — the
 * endpoint accepts exactly this one MeSH object inbound, because a descriptor UI
 * is public NLM vocabulary while a compiled Boolean is curation. It rides into
 * the MATCHED SET server-side, so it moves `meta.total` and the deep pages with
 * the page itself, which is the equality the rail's counts depend on.
 */
type PracticeFeedQuery = {
  corpus: PracticeRailCorpus;
  descriptorUi: string | null;
  /**
   * The drawer's two axes. Several values of one axis are a UNION; the two axes
   * INTERSECT. Both ride into the matched set server-side alongside the chip, so
   * they move `meta.total` and the deep pages with the page — which is the
   * equality the drawer's counts are drawn against.
   *
   * Empty is "this axis is not filtering", and it is the only representation of
   * it: there is no `null` here, because an absent array and an empty one would
   * be two spellings of one state.
   */
  journalUids: readonly string[];
  publicationTypes: readonly string[];
};

/**
 * The practice arm's feed: the publications ONE of the reader's practices
 * matches, evaluated live in SQL from that profile's MeSH Boolean.
 *
 * TWO endpoints, one per corpus, and at most one of them is enabled per render.
 *
 * There was an earlier aggregate and it is not this one. That one `OR`ed the
 * Booleans into a single predicate and ordered the result globally, so the widest
 * Boolean took the whole first page (`or` compiles to `GREATEST`) and ten
 * practices took eight seconds. This one UNIONs the matched SETS, orders by
 * publication date and reads the reader's first six practices.
 *
 * NOT the personalised `/webapp/private/feed`: no ranking rotation, no seed, no
 * anti-repetition. That is the whole point of the arm, so the hook does not
 * simulate any of it.
 *
 * `null` is what a Room selection passes: in Room mode the page reads the
 * ordinary feed endpoint, and a practice request fired alongside it would both
 * waste a round trip and warm a cache entry nobody is going to read. It is ONE
 * parameter and not a selection plus an `enabled` flag, because the two could
 * never legitimately disagree.
 */
export function usePracticeFeed(
  feed: PracticeFeedQuery | null
): PracticeFeedResult {
  const { contentLang } = useActiveLang();

  // The aggregate takes NEITHER drawer axis: no endpoint counts journal or
  // publication-type values over a union, so there is nothing for a reader to
  // have ticked. The page's resolver keeps them empty rather than this hook
  // dropping them silently — a filter in force that the request omits would be a
  // feed disagreeing with the URL that addressed it.
  const allQuery = useApiInfiniteQuery(
    "/webapp/private/practice-profiles/publications",
    feed === null || feed.corpus.kind !== "all"
      ? false
      : feed.descriptorUi === null
        ? { query: { limit: PAGE_SIZE, language: contentLang } }
        : {
            query: {
              limit: PAGE_SIZE,
              language: contentLang,
              descriptorUi: feed.descriptorUi,
            },
          }
  );

  const oneQuery = useApiInfiniteQuery(
    "/webapp/private/practice-profiles/:uid/publications",
    feed === null || feed.corpus.kind !== "practice"
      ? false
      : {
          param: { uid: feed.corpus.uid },
          // Two explicit shapes rather than one carrying `descriptorUi:
          // undefined`: the parameter is absent from the request or it is a
          // value, and the query KEY has to differ between the two — filtering
          // is a different feed, not the same feed re-read, so it paginates from
          // its own first page.
          //
          // The two axes are sent ALWAYS, empty included, because an empty array
          // has its own wire spelling here (`?journalUids=[]`) and enumerating
          // present/absent across three parameters would be eight shapes of one
          // request.
          query:
            feed.descriptorUi === null
              ? {
                  limit: PAGE_SIZE,
                  language: contentLang,
                  journalUids: [...feed.journalUids],
                  publicationTypes: [...feed.publicationTypes],
                }
              : {
                  limit: PAGE_SIZE,
                  language: contentLang,
                  descriptorUi: feed.descriptorUi,
                  journalUids: [...feed.journalUids],
                  publicationTypes: [...feed.publicationTypes],
                },
        }
  );

  const query =
    feed !== null && feed.corpus.kind === "all" ? allQuery : oneQuery;
  const retryInfiniteQuery = useRetryApiInfiniteQuery();

  switch (query.state) {
    // `feed: null`, which is Room mode. The caller that switched the query off is
    // the same one that is not rendering its result, so the honest report is the
    // one the page already ignores: ready, holding nothing.
    case "idle":
      return {
        state: "ready",
        articles: [],
        majorDescriptorUisByArticleId: new Map(),
        hasNextPage: false,
        canFetchNextPage: false,
        isFetchingNextPage: false,
        fetchNextPage: () => undefined,
      };
    case "pending":
      return { state: "pending" };
    case "error": {
      const { key } = query;
      return {
        state: "error",
        message: query.message,
        status: query.status,
        isRetrying: query.isFetching,
        retry: () => {
          retryInfiniteQuery(key);
        },
      };
    }
    case "success":
      return {
        state: "ready",
        articles: query.docs,
        // Read from the page BODIES rather than the flattened docs, because the
        // sidecar is a sibling of `docs` and infinite scroll holds several pages
        // of both.
        majorDescriptorUisByArticleId: new Map(
          query.bodies.flatMap((body) =>
            body.majorDescriptorUis.map(
              (entry) => [entry.articleId, entry.descriptorUis] as const
            )
          )
        ),
        hasNextPage: query.hasNextPage,
        canFetchNextPage: query.canFetchNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: query.fetchNextPage,
      };
    default:
      throw new Error(query satisfies never);
  }
}
